"use client";

import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/dubai/ui/badge";

const collageImages = [
  {
    src: "/dubai/dubai-business-location1.svg",
    alt: "Dubai business location",
    className:
      "absolute left-[5.72%] top-0 h-[46%] w-[45.39%] rounded-[24px_24px_0_24px] object-cover object-[18%_50%]",
  },
  {
    src: "/dubai/dubai-business-location4.svg",
    alt: "Dubai business relocation",
    className:
      "absolute left-[54.61%] top-[49.45%] h-[46%] w-[45.39%] rotate-180 rounded-[24px_24px_24px_0] object-cover object-[82%_50%]",
  },
  {
    src: "/dubai/dubai-business-location2.svg",
    alt: "Dubai skyline",
    className:
      "absolute left-[54.61%] top-[13.14%] h-[32.85%] w-[30.63%] rotate-90 rounded-[24px_24px_0_24px] object-cover object-[98%_40%]",
  },
  {
    src: "/dubai/dubai-business-location3.svg",
    alt: "Business meeting in Dubai",
    className:
      "absolute left-[16.6%] top-[49.45%] h-[35.22%] w-[34.5%] -rotate-90 rounded-[24px_24px_0_24px] object-cover object-[48%_45%]",
  },
];

export const DubaiBusinessRelocationSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate flex w-full flex-col items-center overflow-hidden bg-black px-4 py-[70px] sm:px-6 lg:px-8"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-[292px] -top-[77px] h-[985px] w-[985px] rounded-[336px] blur-[12.5px] [background:radial-gradient(50%_50%_at_50%_50%,rgba(0,97,254,0.2)_0%,rgba(0,97,254,0)_68%)]"
      />
      <div className="relative flex w-full max-w-[1180px] flex-col items-center justify-center gap-12 min-[1280px]:flex-row min-[1280px]:gap-24">
        <div
          className={`flex w-full min-w-0 flex-1 flex-col items-start gap-[23.2px] transition-[transform,opacity] duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            isVisible ? "translate-x-0 opacity-100" : "-translate-x-16 opacity-0"
          }`}
        >
          <div className="inline-flex items-center justify-center gap-4">
            <Badge className="inline-flex h-auto items-center gap-2 rounded-[99px] border border-[#0B3CFF4c] bg-[#0B3CFF33] px-2.5 py-1 pl-1.5 font-suisse-intl text-sm font-light leading-[17px] text-blue-200 shadow-none backdrop-blur-[2.5px]">
              <img
                className="h-5 w-5"
                src="/dubai/box.svg"
                alt=""
                aria-hidden="true"
              />
              <span>Build your global base</span>
            </Badge>
            <img
              className="hidden h-[6px] w-[114px] sm:block"
              src="/dubai/right-design.svg"
              alt=""
              aria-hidden="true"
            />
          </div>
          <h2
            className="w-full max-w-[600px] bg-clip-text font-suisse-intl text-[clamp(42px,5.1vw,70px)] font-normal leading-[1] tracking-0 text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent]"
            style={{
              backgroundImage:
                "linear-gradient(300deg,#1991fe 0%,#FFFFFF 100%)",
            }}
          >
            Planning to <br></br> Move Your Business
            <br />
            to Dubai?
          </h2>
          <p className="w-full max-w-[688px] pt-[7.78px] font-suisse-intl text-base font-normal leading-[25.6px] tracking-0 text-[#a0a4b3]">
            Thinking about moving your business to Dubai? Most founders look at{" "}
            <br className="hidden sm:block" />
            Dubai for more than just a business license. They want a global base
            for{" "}
            <br className="hidden sm:block" />
            their company, and often, a better place to live. If you are coming
            from{" "}
            <br className="hidden sm:block" />
            places like the UK, Europe, or Australia, the tax savings are a
            massive{" "}
            <br className="hidden sm:block" />
            draw. Just keep in mind that your actual tax setup depends on where
            you{" "}
            <br className="hidden sm:block" />
            live and where you manage the business day-to-day.
          </p>
        </div>
        <figure
          className={`relative aspect-[542/548] w-full max-w-[542px] shrink-0 transition-[transform,opacity] duration-1000 delay-150 ease-[cubic-bezier(0.22,1,0.36,1)] min-[1280px]:flex-1 ${
            isVisible ? "translate-x-0 opacity-100" : "translate-x-16 opacity-0"
          }`}
        >
          <div
            aria-hidden="true"
            className="absolute left-[87.82%] top-[3.1%] h-[8.58%] w-[8.67%] rounded-full bg-[#ff8e8e]"
          />
          <div
            aria-hidden="true"
            className="absolute left-[38.56%] top-[94.3%] h-[5.66%] w-[5.72%] rounded-full bg-[#8fdeff]"
          />
          {collageImages.map((image) => (
            <img
              key={image.src}
              className={image.className}
              src={image.src}
              alt={image.alt}
            />
          ))}

          <div
            aria-hidden="true"
            className="absolute left-0 top-[57.48%] h-[7.12%] w-[7.2%] rounded-full bg-[#ffd646]"
          />
        </figure>
      </div>
    </section>
  );
};
